import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import type {
  SupportedCurrency,
  TokenPaymentMethod,
} from '@sorare/core/src/__generated__/globalTypes';
import { Currency, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { HeadlineXL } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { glossary, payment } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql/index.ts';

import { LazyPaymentProvider } from '#src/components/buyActions/LazyPaymentProvider/index.tsx';
import { useAcceptOffer } from '#src/hooks/offers/useAcceptOffer.ts';
import { usePollInGameCurrencyPackDelivery } from '#src/hooks/primaryBuy/usePollInGameCurrencyPackDelivery.tsx';

import { PurchasedPacksReveal } from './PurchasedPacksReveal/index.tsx';
import { SuccessBody } from './SuccessBody/index.tsx';
import { TokenPreview } from './TokenPreview/index.tsx';
import type { PurchasableItemPaymentDialog_purchasableItem } from './__generated__/index.graphql.ts';

type Props = {
  purchasableItem: PurchasableItemPaymentDialog_purchasableItem;
  onClose: () => void;
  isSpecialBundle?: boolean;
};

export const PurchasableItemPaymentDialog = ({
  purchasableItem,
  onClose,
  isSpecialBundle,
}: Props) => {
  const { toMonetaryAmount } = useMonetaryAmount();
  const { postMessage, isMobileApp, isIosApp } = useIsMobileApp();
  const [paymentSucceeded, setPaymentSucceeded] = useState(false);
  const [purchasedOfferId, setPurchasedOfferId] = useState<string | null>(null);
  const acceptOffer = useAcceptOffer();
  const {
    flags: {
      enableIosPostGemsPurchase = false,
      stayOnWebForSpecialBundles = false,
      disablePaypalInWebview = false,
    },
  } = useFeatureFlags();

  const stayOnWeb = isSpecialBundle && stayOnWebForSpecialBundles;

  const onPaymentSucceeded = (success: boolean, offerIdArg?: string | null) => {
    if (success) {
      const resolvedOfferId = offerIdArg ?? purchasedOfferId;
      if (
        isIosApp &&
        resolvedOfferId &&
        !stayOnWeb &&
        (isType(purchasableItem, 'BundledPurchasableItem') ||
          enableIosPostGemsPurchase)
      ) {
        postMessage('refreshGemsBalanceWithOfferId', {
          offerId: resolvedOfferId,
        });
      } else {
        setPaymentSucceeded(true);
      }
    }
  };

  const { polling, startPolling } =
    usePollInGameCurrencyPackDelivery(onPaymentSucceeded);

  const price = toMonetaryAmount(purchasableItem.price);

  const buyWithWallet = async ({
    supportedCurrency,
    tokenPaymentMethod,
    applePayPaymentToken,
  }: {
    supportedCurrency: SupportedCurrency;
    tokenPaymentMethod?: TokenPaymentMethod;
    applePayPaymentToken?: ApplePayJS.ApplePayPaymentToken;
  }) => {
    const { errors, deal } = await acceptOffer({
      supportedCurrency,
      purchasableItemId: purchasableItem.id,
      useAvailableCredits: false,
      attemptReference: null,
      tokenPaymentMethod,
      applePayPaymentToken,
    });
    if (deal) {
      setPurchasedOfferId(deal.id);
      onPaymentSucceeded(true, deal.id);
      return { err: undefined };
    }
    return { err: errors };
  };

  const onCloseSuccessDialog = () => {
    setPaymentSucceeded(false);
    setPurchasedOfferId(null);
    if (isMobileApp) postMessage('refreshGemsBalance');
    onClose();
  };

  if (price.isZero) return null;

  if (paymentSucceeded) {
    return (
      <>
        {/* temporary hide success dialog for bundled purchases
         * as pack opening dialogs are opened instead
         */}
        {!isType(purchasableItem, 'BundledPurchasableItem') && (
          <Dialog
            open
            maxWidth="xs"
            fullWidth
            hideHeader
            onClose={onCloseSuccessDialog}
            footer={
              <Button
                size="medium"
                color="secondary"
                onClick={onCloseSuccessDialog}
                fullWidth
              >
                <FormattedMessage {...glossary.continue} />
              </Button>
            }
          >
            <SuccessBody
              purchasableItem={purchasableItem}
              onClose={onCloseSuccessDialog}
            />
          </Dialog>
        )}
        {!!purchasedOfferId && (
          <PurchasedPacksReveal
            offerId={purchasedOfferId}
            onAllRevealed={onCloseSuccessDialog}
          />
        )}
      </>
    );
  }

  return (
    <LazyPaymentProvider
      paymentProps={{
        cardPaymentProvider: purchasableItem.cardPaymentProvider,
        canChangeRefCurrency: true,
        usePaypal: !(isMobileApp && disablePaypalInWebview),
        onSuccess: () => startPolling(),
        onSubmit: buyWithWallet,
        cta: payment.confirmAndPay,
        applyPaymentFees: true,
        canUseConversionCredit: false,
        currencies: [Currency.FIAT, Currency.ETH],
        sport: Sport.FOOTBALL,
        useKlarna: false,
        objectId: purchasableItem.id,
        objectType: 'inGameCurrencyPack',
        price,
        afterPrepareAcceptOffer: setPurchasedOfferId,
      }}
      paymentBoxProps={{
        loadingPolling: polling,
      }}
      paymentDialogProps={{
        onClose,
        title: (
          <HeadlineXL as="h5">
            <FormattedMessage {...payment.paymentBoxTitle} />
          </HeadlineXL>
        ),
        tokenPreview: <TokenPreview purchasableItem={purchasableItem} />,
      }}
    />
  );
};

PurchasableItemPaymentDialog.fragments = {
  purchasableItem: gql`
    fragment PurchasableItemPaymentDialog_purchasableItem on PurchasableItemInterface {
      slug
      id
      cardPaymentProvider
      price {
        ...useMonetaryAmount_monetaryAmount
      }
      ...TokenPreview_purchasableItem
      ...SuccessBody_purchasableItem
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
    ${TokenPreview.fragments.purchasableItem}
    ${SuccessBody.fragments.purchasableItem}
  ` as TypedDocumentNode<PurchasableItemPaymentDialog_purchasableItem>,
};
